/**
 * QultureUp / Adaptation engagement classification — the SINGLE canonical source
 * of the band rule (thresholds + decision) shared by:
 *   - platform-api `classifyEngagement` (layers daysInactive / needsAttention on top)
 *   - business-web Health Dashboard (legend copy + inverse-priority Coaching order)
 *
 * Keeping the thresholds + the band decision here means the admin-facing legend
 * can NEVER drift from the rule that actually classifies a talent: the legend
 * interpolates `ENGAGEMENT_THRESHOLDS`, and the classifier reads the same numbers.
 *
 * The rule combines three signals so a talent's band reflects real momentum, not
 * just recency:
 *   - recency      — days since last completed mission (`daysInactive`)
 *   - consistency  — `currentStreak` (consecutive active days)
 *   - momentum     — `weeklyMissionsCompleted` (missions completed in the last 7 days)
 */

export type AdaptationEngagementLevel = 'thriving' | 'active' | 'slowing' | 'at_risk' | 'inactive';

/**
 * The canonical thresholds. The FE legend interpolates these exact numbers, so
 * tuning the rule here updates the explanation shown to the admin automatically.
 */
export const ENGAGEMENT_THRESHOLDS = {
  /** Idle strictly beyond this many days → `inactive`. */
  inactiveDays: 14,
  /** Idle strictly beyond this many days → `at_risk`. */
  atRiskDays: 5,
  /** Idle strictly beyond this many days → `slowing` (or `at_risk` if no weekly momentum). */
  slowingDays: 2,
  /** A streak strictly above this, plus weekly momentum, qualifies for `thriving`. */
  thrivingStreak: 8,
  /** Minimum missions completed in the last 7 days to count as having momentum. */
  momentumMin: 1,
} as const;

export interface EngagementBandInput {
  /** Days since last completed mission; `null` = never active. */
  daysInactive: number | null;
  /** Consecutive active days (canonical QultureUp streak). */
  currentStreak: number;
  /** Missions completed in the last 7 days (momentum proxy). */
  weeklyMissionsCompleted: number;
}

/**
 * Pure band decision from the three signals. No daysInactive computation here —
 * the caller derives `daysInactive` from `lastActivityDate` and passes it in.
 *
 * Rule (most-urgent first):
 *   inactive : idle > 14d
 *   at_risk  : idle > 5d, OR (idle > 2d AND no progress in the last 7d — stalled)
 *   slowing  : idle > 2d, OR (recent but zero momentum and no streak)
 *   thriving : idle ≤ 2d AND streak > 8 AND momentum ≥ 1
 *   active   : otherwise
 */
export function classifyEngagementBand(input: EngagementBandInput): AdaptationEngagementLevel {
  const { currentStreak, weeklyMissionsCompleted } = input;
  const idle = input.daysInactive ?? Number.POSITIVE_INFINITY;
  const T = ENGAGEMENT_THRESHOLDS;

  if (idle > T.inactiveDays) return 'inactive';
  if (idle > T.atRiskDays) return 'at_risk';
  if (idle > T.slowingDays) {
    // 3–5 days idle: no progress this week → stalled (at_risk), else slowing.
    return weeklyMissionsCompleted === 0 ? 'at_risk' : 'slowing';
  }
  // Recent (≤ 2 days idle):
  if (weeklyMissionsCompleted === 0 && currentStreak === 0) return 'slowing';
  if (currentStreak > T.thrivingStreak && weeklyMissionsCompleted >= T.momentumMin) {
    return 'thriving';
  }
  return 'active';
}

export interface EngagementBandMeta {
  level: AdaptationEngagementLevel;
  /** Higher = more urgent. Drives the inverse-priority Coaching Center order. */
  urgency: number;
}

/**
 * Bands in inverse-priority order (most urgent first) with their urgency weight.
 * The Coaching Center renders sections in this order; the legend lists them too.
 */
export const ENGAGEMENT_BANDS: readonly EngagementBandMeta[] = [
  { level: 'inactive', urgency: 5 },
  { level: 'at_risk', urgency: 4 },
  { level: 'slowing', urgency: 3 },
  { level: 'active', urgency: 2 },
  { level: 'thriving', urgency: 1 },
];

/** Urgency weight for a band (used to sort talents most-urgent-first). */
export function engagementUrgency(level: AdaptationEngagementLevel): number {
  return ENGAGEMENT_BANDS.find((b) => b.level === level)?.urgency ?? 0;
}

// ── Cohort Adaptation health ────────────────────────────────────────────

/** What each engagement band contributes to a cohort's Adaptation health (0–100). */
export const ADAPTATION_HEALTH_WEIGHT: Record<AdaptationEngagementLevel, number> = {
  thriving: 100,
  active: 80,
  slowing: 50,
  at_risk: 25,
  inactive: 0,
};

/** Every health band the Adaptation overview can send. */
export const ADAPTATION_HEALTH_BANDS = ['healthy', 'attention', 'critical'] as const;
export type AdaptationHealthBand = (typeof ADAPTATION_HEALTH_BANDS)[number];

/** A score this high or better is healthy; this or better needs attention; else critical. */
export const ADAPTATION_HEALTH_LOWER_BOUNDS = { healthy: 70, attention: 50 } as const;

/**
 * A cohort's Adaptation health — the average of its started talents'
 * engagement weights, and its band. Computed by the server and drawn by the
 * page, which used to average and grade it itself. `null` with nobody started:
 * an empty cohort is not a 0 % red one.
 */
export function adaptationHealth(
  levels: readonly AdaptationEngagementLevel[]
): { score: number; band: AdaptationHealthBand } | null {
  if (levels.length === 0) return null;
  const score = Math.round(
    levels.reduce((sum, level) => sum + ADAPTATION_HEALTH_WEIGHT[level], 0) / levels.length
  );
  const band: AdaptationHealthBand =
    score >= ADAPTATION_HEALTH_LOWER_BOUNDS.healthy
      ? 'healthy'
      : score >= ADAPTATION_HEALTH_LOWER_BOUNDS.attention
        ? 'attention'
        : 'critical';
  return { score, band };
}
